import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import translations from './translations';

const DEFAULT_LANGUAGE = 'uk';

function resolve(dict, path) {
  if (typeof path !== 'string' || !path) return undefined;
  const parts = path.split('.');
  let node = dict;
  for (const key of parts) {
    if (node && typeof node === 'object' && key in node) {
      node = node[key];
    } else {
      return undefined;
    }
  }
  if (typeof node === 'string' || Array.isArray(node)) return node;
  return undefined;
}

function interpolate(template, vars) {
  if (typeof template !== 'string') return template;
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : `{{${name}}}`
  );
}

export function useTranslation() {
  const { language, setLanguage } = useAuth();
  const lang = translations[language] ? language : DEFAULT_LANGUAGE;

  const t = useCallback(
    (key, varsOrFallback, maybeFallback) => {
      const hasVars = varsOrFallback && typeof varsOrFallback === 'object';
      const vars = hasVars ? varsOrFallback : undefined;
      const fallback = hasVars ? maybeFallback : varsOrFallback;

      if (typeof key !== 'string' || !key) {
        return fallback !== undefined ? interpolate(String(fallback), vars) : '';
      }

      const value = resolve(translations[lang], key);
      if (value !== undefined) return interpolate(value, vars);
      if (lang !== DEFAULT_LANGUAGE) {
        const fromDefault = resolve(translations[DEFAULT_LANGUAGE], key);
        if (fromDefault !== undefined) return interpolate(fromDefault, vars);
      }
      return fallback !== undefined ? interpolate(fallback, vars) : key;
    },
    [lang]
  );

  return { t, language: lang, setLanguage };
}
